import { useEffect, useMemo, useRef } from "react";
import {
  IProductVariant,
  ProductStatus,
  defaultPaginationOptions,
  endpoints,
} from "muminsmart-utils";
import ProductCard from "./ProductCard";
import RelatedProductsSkeleton from "./RelatedProductsSkeleton";
import { ProductCardSkeleton } from "./ProductListingSkeleton";
import { useRtkInfiniteList } from "@/lib/hooks/useRtkInfiniteList";
import { ProductSectionTitle } from "./ProductDetails";

interface RelatedProductsProps {
  categoryId: string;
  subCategoryId: string;
  excludeProductId?: string;
}

const RelatedProducts = ({
  categoryId,
  subCategoryId,
  excludeProductId,
}: RelatedProductsProps) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const baseParams = useMemo(
    () => ({
      ...defaultPaginationOptions,
      sortBy: "createdAt",
      sortOrder: "DESC",
      categoryId,
      subCategoryId,
      relations: "product,variantAttributes",
      isActive: true,
      inStock: true,
      status: ProductStatus.Verified,
    }),
    [categoryId, subCategoryId],
  );

  const {
    items: rawItems,
    page,
    hasMore,
    isInitialLoading,
    isLoadingMore,
    loadPage,
  } = useRtkInfiniteList<IProductVariant>({
    endpoint: endpoints.productVariant.paginated,
    baseParams,
    errorMessage: "Failed to fetch related products",
  });

  const items = useMemo(
    () => rawItems.filter((variant) => variant.productId !== excludeProductId),
    [excludeProductId, rawItems],
  );

  useEffect(() => {
    void loadPage(1, true);
  }, [loadPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!node || !root) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          void loadPage(page + 1);
        }
      },
      { root, threshold: 0.1 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, loadPage, page]);

  if (!isInitialLoading && items.length === 0) {
    return null;
  }

  return (
    <section className="bg-white px-4 py-5 space-y-2">
      <ProductSectionTitle title="People Also Viewed" />

      {isInitialLoading ? (
        <RelatedProductsSkeleton />
      ) : (
        <div
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto pb-2"
        >
          {items.map((variant) => (
            <div key={variant.id} className="w-40 shrink-0">
              <ProductCard variant={variant} product={variant.product} />
            </div>
          ))}
          {hasMore ? (
            <div
              ref={sentinelRef}
              className="w-1 shrink-0"
              aria-hidden="true"
            />
          ) : null}
          {isLoadingMore ? (
            <div className="w-40 shrink-0">
              <ProductCardSkeleton />
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
};

export default RelatedProducts;
